/**
 * @fileoverview Job-level Xero report import dialog (MOR-555 Phase 3).
 *
 * Gated on the user's account-level Xero connection (see the connected
 * accounts section). Binds one Xero organisation per job — different jobs
 * can bind different organisations, and the binding can be changed here at
 * any time. The organisation list is every tenant the connected Xero login
 * has authorised; the "Connect another organisation" action re-runs the
 * OAuth consent to authorise an additional one (existing authorisations are
 * kept). Reports import as documents — structured JSON rendered to text, so
 * the Reducto parse step is skipped entirely. Opened from the inline
 * "Import reports" action in the connected accounts card.
 */

import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { useAction } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { toast } from "sonner";
import { AlertTriangle, Building2, Loader2, Plus } from "lucide-react";
import { cn } from "@/utils/misc";

export interface XeroTenantBinding {
  tenantId: string;
  tenantName: string;
}

type ReportSlug =
  | "BalanceSheet"
  | "ProfitAndLoss"
  | "TrialBalance"
  | "BankSummary"
  | "ExecutiveSummary"
  | "BudgetSummary";

const REPORTS: Array<{
  slug: ReportSlug;
  label: string;
  dates: "asAt" | "range";
  defaultOn: boolean;
}> = [
  {
    slug: "BalanceSheet",
    label: "Balance Sheet",
    dates: "asAt",
    defaultOn: true,
  },
  {
    slug: "ProfitAndLoss",
    label: "Profit and Loss",
    dates: "range",
    defaultOn: true,
  },
  {
    slug: "TrialBalance",
    label: "Trial Balance",
    dates: "asAt",
    defaultOn: true,
  },
  {
    slug: "BankSummary",
    label: "Bank Summary",
    dates: "range",
    defaultOn: true,
  },
  {
    slug: "ExecutiveSummary",
    label: "Executive Summary",
    dates: "asAt",
    defaultOn: false,
  },
  {
    slug: "BudgetSummary",
    label: "Budget Summary",
    dates: "asAt",
    defaultOn: false,
  },
];

interface ReportRowState {
  selected: boolean;
  asAtDate: string;
  fromDate: string;
  toDate: string;
}

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function XeroImportDialog({
  jobId,
  currentTenant,
  onClose,
}: {
  jobId: Id<"jobs">;
  /** The organisation currently bound to this job, if any. */
  currentTenant: XeroTenantBinding | null;
  onClose: () => void;
}) {
  const [step, setStep] = useState<"tenant" | "reports">(
    currentTenant ? "reports" : "tenant",
  );
  const [boundTenant, setBoundTenant] = useState<XeroTenantBinding | null>(
    currentTenant,
  );
  const [tenants, setTenants] = useState<XeroTenantBinding[]>([]);
  // Starting on the tenant step means the fetch begins on mount — render
  // the spinner from the first frame rather than flashing an empty list.
  const [loadingTenants, setLoadingTenants] = useState(!currentTenant);
  const [connecting, setConnecting] = useState(false);
  const popupPollRef = useRef<number | null>(null);

  const today = isoDate(new Date());
  const yearAgo = isoDate(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000));
  const [rows, setRows] = useState<Record<ReportSlug, ReportRowState>>(
    () =>
      Object.fromEntries(
        REPORTS.map((report) => [
          report.slug,
          {
            selected: report.defaultOn,
            asAtDate: today,
            fromDate: yearAgo,
            toDate: today,
          },
        ]),
      ) as Record<ReportSlug, ReportRowState>,
  );
  const [importing, setImporting] = useState(false);

  const selectedCount = REPORTS.filter((r) => rows[r.slug].selected).length;

  const listTenants = useAction(
    api.integrations.workOsSync.xeroActions.listXeroTenants,
  );
  const getConnectUrl = useAction(
    api.integrations.workOsSync.browseActions.getConnectUrl,
  );
  const importReports = useAction(
    api.integrations.workOsSync.xeroActions.importXeroReports,
  );
  const saveTenant = useMutation({
    mutationFn: useConvexMutation(
      api.integrations.workOsSync.mutations.saveXeroTenant,
    ),
    onError: (error) => toast.error(error.message),
  });

  function bindTenant(tenant: XeroTenantBinding) {
    saveTenant.mutate(
      { jobId, tenantId: tenant.tenantId, tenantName: tenant.tenantName },
      {
        onSuccess: () => {
          setBoundTenant(tenant);
          setStep("reports");
        },
      },
    );
  }

  async function refreshTenants() {
    setLoadingTenants(true);
    try {
      const result = await listTenants({});
      if (!result.ok) {
        toast.error(
          "message" in result && result.message
            ? result.message
            : "Connect your Xero account first.",
        );
        onClose();
        return;
      }
      setTenants(result.tenants);
    } finally {
      setLoadingTenants(false);
    }
  }

  // Load the organisation list whenever the tenant step is shown.
  useEffect(() => {
    if (step === "tenant") void refreshTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per entry to the tenant step: refreshTenants closes over the onClose prop and gets a new identity each render, so depending on it would refetch the tenant list on every re-render while the step is open
  }, [step]);

  // Clear any popup poll on unmount.
  useEffect(() => {
    return () => {
      if (popupPollRef.current !== null)
        window.clearInterval(popupPollRef.current);
    };
  }, []);

  /**
   * Re-runs the Xero OAuth consent so the user can authorise an ADDITIONAL
   * organisation. The account is already connected, so the status can't
   * flip — just wait for the popup to close, then re-list tenants.
   */
  async function handleConnectAnotherOrganisation() {
    setConnecting(true);
    try {
      const url = await getConnectUrl({ provider: "xero" });
      const popup = window.open(
        url,
        "morgan-pipes-connect",
        "width=600,height=720",
      );
      if (!popup) {
        toast.error("Popup blocked — allow popups for this site and retry.");
        setConnecting(false);
        return;
      }
      popupPollRef.current = window.setInterval(() => {
        if (popup.closed) {
          if (popupPollRef.current !== null)
            window.clearInterval(popupPollRef.current);
          popupPollRef.current = null;
          setConnecting(false);
          void refreshTenants();
        }
      }, 1500);
    } catch (error) {
      setConnecting(false);
      toast.error(
        error instanceof Error ? error.message : "Failed to start connection",
      );
    }
  }

  function patchRow(slug: ReportSlug, patch: Partial<ReportRowState>) {
    setRows((previous) => ({
      ...previous,
      [slug]: { ...previous[slug], ...patch },
    }));
  }

  async function handleImport() {
    setImporting(true);
    try {
      const requests = REPORTS.filter(
        (report) => rows[report.slug].selected,
      ).map((report) => {
        const row = rows[report.slug];
        return report.dates === "asAt"
          ? { slug: report.slug, asAtDate: row.asAtDate }
          : { slug: report.slug, fromDate: row.fromDate, toDate: row.toDate };
      });
      const results = await importReports({ jobId, reports: requests });
      const succeeded = results.filter((r) => r.ok);
      const failed = results.filter((r) => !r.ok);
      if (succeeded.length > 0) {
        toast.success(
          `Imported ${succeeded.map((r) => r.report).join(", ")} — processing started`,
        );
      }
      for (const failure of failed) {
        toast.error(`${failure.report}: ${failure.message ?? "failed"}`);
      }
      if (failed.length === 0) onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Import failed");
    } finally {
      setImporting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Import Xero reports</DialogTitle>
        </DialogHeader>

        {step === "tenant" && (
          <div className="space-y-3">
            {loadingTenants || saveTenant.isPending ? (
              <div className="flex h-20 items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Choose the Xero organisation to import from for this job. Each
                  job can use a different organisation.
                </p>
                <div className="max-h-64 space-y-2 overflow-y-auto">
                  {tenants.length === 0 ? (
                    <p className="rounded border px-3 py-4 text-center text-sm text-muted-foreground">
                      No Xero organisations are authorised yet — connect one
                      below.
                    </p>
                  ) : (
                    tenants.map((tenant) => (
                      <button
                        key={tenant.tenantId}
                        type="button"
                        className="flex w-full items-center gap-2.5 rounded border px-3 py-2 text-left text-sm hover:bg-muted"
                        onClick={() => bindTenant(tenant)}
                      >
                        <Building2 className="h-4 w-4 shrink-0 text-sky-600" />
                        <span className="truncate">{tenant.tenantName}</span>
                        {tenant.tenantId === boundTenant?.tenantId && (
                          <Badge
                            color="blue"
                            variant="soft"
                            size="sm"
                            className="ml-auto shrink-0"
                          >
                            Current
                          </Badge>
                        )}
                      </button>
                    ))
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">
                    can't see the organisation you need?
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>

                <Button
                  variant="outline"
                  className="w-full"
                  disabled={connecting}
                  onClick={() => void handleConnectAnotherOrganisation()}
                >
                  {connecting ? (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="mr-1 h-3.5 w-3.5" />
                  )}
                  Connect another Xero organisation
                </Button>
                <p className="text-xs text-muted-foreground">
                  Xero will ask you to authorise access to another organisation.
                  Organisations you've already connected stay connected.
                </p>

                {boundTenant && (
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setStep("reports")}
                    >
                      Back
                    </Button>
                  </DialogFooter>
                )}
              </>
            )}
          </div>
        )}

        {step === "reports" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/40 px-3 py-2">
              <div className="flex min-w-0 items-center gap-2 text-sm">
                <Building2 className="h-4 w-4 shrink-0 text-sky-600" />
                <span className="text-xs text-muted-foreground">
                  Organisation:
                </span>
                <span className="truncate font-medium">
                  {boundTenant?.tenantName ?? "—"}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0"
                onClick={() => setStep("tenant")}
              >
                Change organisation
              </Button>
            </div>
            <div className="flex items-start gap-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">
                  You don't have access to the full reports
                </p>
                <p className="text-xs text-foreground/80">
                  We have limited report access on unpaid accounts. Reach out to
                  your account manager to upgrade to a paid account and get the
                  full reports.
                </p>
              </div>
            </div>
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50 text-left text-xs text-muted-foreground">
                    <th className="w-[34%] px-3 py-2 font-medium">Report</th>
                    <th className="w-[33%] px-3 py-2 font-medium">From</th>
                    <th className="w-[33%] px-3 py-2 font-medium">
                      To / As at
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {REPORTS.map((report) => {
                    const row = rows[report.slug];
                    return (
                      <tr
                        key={report.slug}
                        className={cn(
                          "border-b last:border-b-0",
                          !row.selected && "bg-muted/30",
                        )}
                      >
                        <td className="px-3 py-2 align-middle">
                          <label className="flex cursor-pointer items-center gap-2.5">
                            <Checkbox
                              checked={row.selected}
                              onCheckedChange={(checked) =>
                                patchRow(report.slug, {
                                  selected: checked === true,
                                })
                              }
                            />
                            <span
                              className={cn(
                                "font-medium",
                                !row.selected && "text-muted-foreground",
                              )}
                            >
                              {report.label}
                            </span>
                          </label>
                        </td>
                        <td className="px-3 py-2">
                          {report.dates === "range" ? (
                            <DatePicker
                              value={row.fromDate}
                              disabled={!row.selected}
                              onChange={(value) =>
                                patchRow(report.slug, { fromDate: value })
                              }
                              className="w-full max-w-44"
                            />
                          ) : (
                            <span className="pl-3 text-muted-foreground">
                              —
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {report.dates === "range" ? (
                            <DatePicker
                              value={row.toDate}
                              disabled={!row.selected}
                              onChange={(value) =>
                                patchRow(report.slug, { toDate: value })
                              }
                              className="w-full max-w-44"
                            />
                          ) : (
                            <DatePicker
                              value={row.asAtDate}
                              disabled={!row.selected}
                              onChange={(value) =>
                                patchRow(report.slug, { asAtDate: value })
                              }
                              className="w-full max-w-44"
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <DialogFooter className="items-center">
              <span className="mr-auto text-xs text-muted-foreground">
                Re-importing a report with the same dates replaces the previous
                version.
              </span>
              <Button
                disabled={importing || selectedCount === 0}
                onClick={() => void handleImport()}
              >
                {importing && (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                )}
                Import {selectedCount} report{selectedCount === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
